import { useState, useMemo, useEffect } from "react";
import {
  Search,
  Plus,
  ShoppingCart,
  Truck,
  Clock,
  CheckCircle2,
} from "lucide-react";
import StatusHistoryModal from "../../components/StatusHistoryModal";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Tabs,
  Select,
  Spin,
  Empty,
} from "antd";
import dayjs from "dayjs";
import {
  IPurchaseOrder,
  IPOCreatePayload,
  IPOLineItem,
  TPOStatus,
} from "../../../types/api/purchase-order";
import { IQuote } from "../../../types/api/quote";
import {
  usePOList,
  usePOCreate,
  usePOUpdate,
  usePODelete,
  usePOUpdateStatus,
  usePOCancel,
  usePOGenerateShareLink,
} from "../../../hooks/api/purchase-orders/hooks";
import { useQuoteList } from "../../../hooks/api/quotes/hooks";
import { useMessageApi } from "../../../../src/provider/AntdNotification";
import axios from "../../../lib/axios";
import POTable from "./POTable";

const STATUS_CFG: Record<TPOStatus, { cls: string; label: string }> = {
  draft: {
    cls: "bg-slate-50 text-slate-500 border border-slate-200",
    label: "Draft",
  },
  sent: {
    cls: "bg-blue-50 text-blue-700 border border-blue-200",
    label: "Sent",
  },
  acknowledged: {
    cls: "bg-indigo-50 text-indigo-700 border border-indigo-200",
    label: "Acknowledged",
  },
  partially_fulfilled: {
    cls: "bg-amber-50 text-amber-700 border border-amber-200",
    label: "Partially Fulfilled",
  },
  fulfilled: {
    cls: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    label: "Fulfilled",
  },
  cancelled: {
    cls: "bg-red-50 text-red-600 border border-red-200",
    label: "Cancelled",
  },
};

// Next status label shown on the advance button
const NEXT_STATUS_LABEL: Partial<Record<TPOStatus, string>> = {
  draft: "Mark as Sent",
  sent: "Mark as Acknowledged",
  acknowledged: "Mark as Fulfilled",
  partially_fulfilled: "Mark as Fulfilled",
};

const NEXT_STATUS_VALUE: Partial<Record<TPOStatus, TPOStatus>> = {
  draft: "sent",
  sent: "acknowledged",
  acknowledged: "fulfilled",
  partially_fulfilled: "fulfilled",
};

// For acknowledged, allow choosing partial or full
const ACKNOWLEDGED_OPTS: { value: TPOStatus; label: string }[] = [
  { value: "partially_fulfilled", label: "Partially Fulfilled" },
  { value: "fulfilled", label: "Fulfilled" },
];

const TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "acknowledged", label: "Acknowledged" },
  { key: "partially_fulfilled", label: "Partially Fulfilled" },
  { key: "fulfilled", label: "Fulfilled" },
];

const VAT_OPTS = [
  { value: 20, label: "20% Standard" },
  { value: 5, label: "5% Reduced" },
  { value: 0, label: "0% Zero" },
];

const ACCEPTED_QUOTE_Q = { page: 1, limit: 200, status: "accepted" };

function fmt(n: number | string | undefined | null) {
  const v = Number(n ?? 0);
  return `£${v.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const SummaryCard = ({
  icon,
  iconBg,
  value,
  label,
  sub,
}: {
  icon: React.ReactNode;
  iconBg: string;
  value: string | number;
  label: string;
  sub?: string;
}) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
    <div
      className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
    >
      {icon}
    </div>
    <div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-800 leading-tight">
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

// Inline line items editor — same pattern as QuotesPage
const LineItemsEditor = ({
  value = [],
  onChange,
}: {
  value?: IPOLineItem[];
  onChange?: (items: IPOLineItem[]) => void;
}) => {
  function update(idx: number, field: keyof IPOLineItem, val: any) {
    const next = value.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: val };
      updated.line_total =
        Math.round(
          Number(updated.quantity ?? 0) * Number(updated.unit_price ?? 0) * 100,
        ) / 100;
      return updated;
    });
    onChange?.(next);
  }

  function addRow() {
    onChange?.([
      ...value,
      { description: "", quantity: 1, unit_price: 0, vat_rate: 20, line_total: 0 },
    ]);
  }

  function removeRow(idx: number) {
    onChange?.(value.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
              <th className="text-left px-3 py-2 w-1/2">Description</th>
              <th className="text-right px-3 py-2 w-16">Qty</th>
              <th className="text-right px-3 py-2 w-28">Unit Price</th>
              <th className="text-right px-3 py-2 w-20">VAT %</th>
              <th className="text-right px-3 py-2 w-28">Total</th>
              <th className="px-2 py-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {value.map((item, idx) => (
              <tr key={idx} className="border-t border-slate-100">
                <td className="px-2 py-1.5">
                  <Input
                    size="small"
                    value={item.description}
                    onChange={(e) => update(idx, "description", e.target.value)}
                    placeholder="Line item description"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <InputNumber
                    size="small"
                    className="w-full"
                    min={0}
                    value={item.quantity}
                    onChange={(v) => update(idx, "quantity", v ?? 0)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <InputNumber
                    size="small"
                    className="w-full"
                    min={0}
                    prefix="£"
                    value={item.unit_price}
                    onChange={(v) => update(idx, "unit_price", v ?? 0)}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Select
                    size="small"
                    className="w-full"
                    value={item.vat_rate}
                    options={VAT_OPTS}
                    onChange={(v) => update(idx, "vat_rate", v)}
                  />
                </td>
                <td className="px-3 py-1.5 text-right text-slate-700 font-medium tabular-nums">
                  {fmt(item.line_total)}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-red-400 hover:text-red-600 text-base leading-none"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {value.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-4 text-slate-400 text-xs"
                >
                  No line items yet. Click "Add Line" to begin.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addRow}
        className="mt-2 text-primary text-sm hover:underline flex items-center gap-1"
      >
        <Plus size={14} /> Add Line
      </button>
    </div>
  );
};

// Advance status modal — handles transitions including acknowledged → partial/full choice
const AdvanceStatusModal = ({
  po,
  qData,
  onClose,
}: {
  po: IPurchaseOrder | null;
  qData: any;
  onClose: () => void;
}) => {
  const messageApi = useMessageApi();
  const [selectedStatus, setSelectedStatus] = useState<TPOStatus | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);

  useEffect(() => {
    if (!po) return;
    // Pre-set the default next status
    const next = NEXT_STATUS_VALUE[po.status];
    setSelectedStatus(next ?? null);
    setDeliveryDate(null);
  }, [po]);

  const { mutate: updateStatus, isPending } = usePOUpdateStatus({
    qData,
    onSuccess: () => {
      messageApi.success("Purchase Order status updated.");
      onClose();
    },
    onError: (err: any) => {
      messageApi.error(
        err?.response?.data?.message ?? "Failed to update status.",
      );
    },
  });

  if (!po) return null;

  const isAcknowledged = po.status === "acknowledged";
  const needsDeliveryDate =
    selectedStatus === "fulfilled" || selectedStatus === "partially_fulfilled";
  const nextLabel = NEXT_STATUS_LABEL[po.status] ?? "Advance";
  const currentCfg = STATUS_CFG[po.status];
  const nextCfg = selectedStatus ? STATUS_CFG[selectedStatus] : null;

  function handleConfirm() {
    if (!selectedStatus) return;
    updateStatus({
      id: po!.id,
      status: selectedStatus,
      delivery_date: deliveryDate ?? undefined,
    });
  }

  return (
    <Modal
      open={!!po}
      onCancel={onClose}
      title={
        <span className="text-base font-semibold">
          Advance PO — {po.po_number}
        </span>
      }
      width={420}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={isPending}
          disabled={!selectedStatus}
          onClick={handleConfirm}
        >
          Confirm
        </Button>,
      ]}
    >
      <div className="py-2 space-y-4">
        <div className="flex items-center gap-3 text-sm">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${currentCfg.cls}`}
          >
            {currentCfg.label}
          </span>
          <span className="text-slate-400">→</span>
          {nextCfg && (
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${nextCfg.cls}`}
            >
              {nextCfg.label}
            </span>
          )}
        </div>

        {isAcknowledged && (
          <div>
            <label className="block text-sm text-slate-600 mb-1">
              Select Next Status
            </label>
            <Select
              className="w-full"
              value={selectedStatus}
              options={ACKNOWLEDGED_OPTS}
              onChange={(v) => setSelectedStatus(v)}
            />
          </div>
        )}

        {needsDeliveryDate && (
          <div>
            <label className="block text-sm text-slate-600 mb-1">
              Delivery Date{" "}
              <span className="text-slate-400 text-xs">(optional)</span>
            </label>
            <DatePicker
              className="w-full"
              onChange={(d) =>
                setDeliveryDate(d ? d.format("YYYY-MM-DD") : null)
              }
            />
          </div>
        )}

        <p className="text-xs text-slate-400">
          {isAcknowledged
            ? "Select whether the order is partially or fully fulfilled."
            : `This will advance the PO to '${selectedStatus ?? "next"}' status. This action cannot be undone.`}
        </p>
      </div>
    </Modal>
  );
};

// Cancel PO modal with optional reason
const CancelModal = ({
  po,
  qData,
  onClose,
}: {
  po: IPurchaseOrder | null;
  qData: any;
  onClose: () => void;
}) => {
  const messageApi = useMessageApi();
  const [reason, setReason] = useState("");

  const { mutate: cancelPO, isPending } = usePOCancel({
    qData,
    onSuccess: () => {
      messageApi.success("Purchase Order cancelled.");
      onClose();
    },
    onError: (err: any) => {
      messageApi.error(
        err?.response?.data?.message ?? "Failed to cancel PO.",
      );
    },
  });

  useEffect(() => {
    if (!po) setReason("");
  }, [po]);

  if (!po) return null;

  return (
    <Modal
      open={!!po}
      onCancel={onClose}
      title={
        <span className="text-base font-semibold">
          Cancel PO — {po.po_number}
        </span>
      }
      width={420}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Back
        </Button>,
        <Button
          key="confirm"
          danger
          type="primary"
          loading={isPending}
          onClick={() => cancelPO({ id: po.id, cancellation_reason: reason || undefined })}
        >
          Cancel PO
        </Button>,
      ]}
    >
      <div className="py-2 space-y-3">
        <p className="text-sm text-slate-600">
          Are you sure you want to cancel{" "}
          <span className="font-semibold">{po.po_number}</span>? This cannot be
          undone.
        </p>
        <div>
          <label className="block text-sm text-slate-600 mb-1">
            Cancellation Reason{" "}
            <span className="text-slate-400 text-xs">(optional)</span>
          </label>
          <Input.TextArea
            rows={3}
            placeholder="Reason for cancellation…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
          />
        </div>
      </div>
    </Modal>
  );
};

const PurchaseOrdersPage = () => {
  const messageApi = useMessageApi();

  const [search, setSearch] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tabFilter, setTabFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPO, setEditingPO] = useState<IPurchaseOrder | null>(null);
  const [advancingPO, setAdvancingPO] = useState<IPurchaseOrder | null>(null);
  const [cancellingPO, setCancellingPO] = useState<IPurchaseOrder | null>(null);
  const [historyPO, setHistoryPO] = useState<IPurchaseOrder | null>(null);
  const [form] = Form.useForm();

  // When a quotation is selected in the form, auto-populate line items
  const [selectedQuotationId, setSelectedQuotationId] = useState<
    number | null
  >(null);

 const queryData = useMemo(() => {
  const q: any = { page, limit };

  if (tabFilter !== "all") {
    q.status = tabFilter;
  }

  if (debouncedSearch.trim()) {
    q.search = debouncedSearch.trim();
  }

  return q;
}, [page, limit, tabFilter, debouncedSearch]);

  const {
    data: listRes,
    isLoading,
    refetchWithQuery: refetch,
  } = usePOList(queryData);

useEffect(() => {
  refetch(queryData);
}, [queryData]);
  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500);

  return () => clearTimeout(timer);
}, [search]);



  // Accepted quotes for the quotation selector
  const { data: acceptedQuoteRes } = useQuoteList(ACCEPTED_QUOTE_Q);
  const acceptedQuotes: IQuote[] =
    (acceptedQuoteRes?.data as IQuote[] | undefined) ?? [];

  const quotationOptions = acceptedQuotes.map((q) => ({
    value: q.id,
    label: `${q.quote_number}${q.title ? ` — ${q.title}` : ""}${q.client_name ? ` (${q.client_name})` : ""}`,
    data: q,
  }));

  const pos: IPurchaseOrder[] =
    (listRes?.data as IPurchaseOrder[] | undefined) ?? [];
  const totalPOs = (listRes as any)?.extra?.total ?? pos.length;

  const { mutate: createPO, isPending: creating } = usePOCreate({
    qData: queryData,
    onSuccess: () => {
      messageApi.success("Purchase Order created.");
      closeModal();
      refetch(queryData);
    },
    onError: (err: any) =>
      messageApi.error(
        err?.response?.data?.message ?? "Failed to create Purchase Order.",
      ),
  });

  const { mutate: updatePO, isPending: updating } = usePOUpdate({
    qData: queryData,
    onSuccess: () => {
      messageApi.success("Purchase Order updated.");
      closeModal();
      refetch(queryData);
    },
    onError: (err: any) =>
      messageApi.error(
        err?.response?.data?.message ?? "Failed to update Purchase Order.",
      ),
  });

  const { mutate: deletePO } = usePODelete({
    qData: queryData,
    onSuccess: () => {
      messageApi.success("Purchase Order deleted.");
      refetch(queryData);
    },
    onError: (err: any) =>
      messageApi.error(
        err?.response?.data?.message ?? "Failed to delete Purchase Order.",
      ),
  });

  const handleDownloadPdf = async (po: IPurchaseOrder) => {
    try {
      const res = await axios.get(`/purchase-orders/pdf/${po.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `po-${po.po_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      messageApi.error("Failed to download PDF.");
    }
  };

  const { mutate: generateShareLink } = usePOGenerateShareLink({
    onSuccess: (token) => {
      const url = `${window.location.origin}/po/${token}`;
      navigator.clipboard.writeText(url).then(
        () => messageApi.success("Share link copied to clipboard!"),
        () => messageApi.info(`Share link: ${url}`),
      );
    },
    onError: () => messageApi.error("Failed to generate share link."),
  });

  // Summary stats from current page data
  const openOrders = pos.filter(
    (p) =>
      p.status === "sent" ||
      p.status === "acknowledged" ||
      p.status === "partially_fulfilled",
  );
  const fulfilled = pos.filter((p) => p.status === "fulfilled");
  const totalValue = pos
    .filter((p) => p.status !== "cancelled")
    .reduce((s, p) => s + Number(p.total ?? 0), 0);

  const tabItems = TABS.map((t) => ({
    key: t.key,
    label: <span className="flex items-center gap-1.5 text-sm">{t.label}</span>,
  }));

  function handleTabChange(key: string) {
    setTabFilter(key);
    setPage(1);
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    setPage(1);
  }

  function openCreate() {
    setEditingPO(null);
    setSelectedQuotationId(null);
    form.resetFields();
    form.setFieldValue("line_items", []);
    form.setFieldValue("vat_rate", 20);
    setModalOpen(true);
  }

  function openEdit(po: IPurchaseOrder) {
    setEditingPO(po);
    setSelectedQuotationId(po.quotation_id ?? null);
    form.setFieldsValue({
      ...po,
      issue_date: po.issue_date ? dayjs(po.issue_date) : null,
      expected_delivery_date: po.expected_delivery_date
        ? dayjs(po.expected_delivery_date)
        : null,
      line_items: po.line_items ?? [],
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingPO(null);
    setSelectedQuotationId(null);
    form.resetFields();
  }

  // When a quotation is selected, auto-fill line items + vat_rate from that quote
  function handleQuotationSelect(quotationId: number) {
    setSelectedQuotationId(quotationId);
    const selectedQuote = acceptedQuotes.find((q) => q.id === quotationId);
    if (selectedQuote) {
      const lineItems: IPOLineItem[] = (selectedQuote.line_items ?? []).map(
        (li) => ({
          description: li.description,
          quantity: Number(li.quantity),
          unit_price: Number(li.unit_price),
          vat_rate: Number(li.vat_rate ?? 20),
          line_total:
            Math.round(
              Number(li.quantity) * Number(li.unit_price) * 100,
            ) / 100,
        }),
      );
      form.setFieldsValue({
        line_items: lineItems,
        vat_rate: selectedQuote.vat_rate ?? 20,
        discount_amount: selectedQuote.discount_amount ?? 0,
      });
    }
  }

  function onFinish(values: any) {
    const payload: any = {
      ...values,
      issue_date: values.issue_date
        ? dayjs(values.issue_date).format("YYYY-MM-DD")
        : undefined,
      expected_delivery_date: values.expected_delivery_date
        ? dayjs(values.expected_delivery_date).format("YYYY-MM-DD")
        : undefined,
    };

    if (editingPO) {
      updatePO({ id: editingPO.id, ...payload });
    } else {
      createPO(payload as IPOCreatePayload);
    }
  }

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">
            Purchase Orders
          </h1>
          <p className="text-slate-400 text-sm">
            Manage procurement from suppliers against accepted quotations
          </p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={15} />}
          onClick={openCreate}
          className="flex items-center gap-1.5 h-9 rounded-lg"
        >
          New PO
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <SummaryCard
          icon={<ShoppingCart size={18} className="text-blue-500" />}
          iconBg="bg-blue-50"
          value={totalPOs}
          label="Total POs"
          sub="all statuses"
        />
        <SummaryCard
          icon={<Clock size={18} className="text-amber-500" />}
          iconBg="bg-amber-50"
          value={openOrders.length}
          label="Open Orders"
          sub="sent, acknowledged or partial"
        />
        <SummaryCard
          icon={<CheckCircle2 size={18} className="text-emerald-500" />}
          iconBg="bg-emerald-50"
          value={fulfilled.length}
          label="Fulfilled"
          sub="completed deliveries"
        />
        <SummaryCard
          icon={<Truck size={18} className="text-indigo-500" />}
          iconBg="bg-indigo-50"
          value={fmt(totalValue)}
          label="Active Value"
          sub="excl. cancelled"
        />
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs
            activeKey={tabFilter}
            onChange={handleTabChange}
            items={tabItems}
            className="flex-1"
            style={{ marginBottom: 0 }}
          />
          <div className="relative w-56">
            <Search
              size={14}
              className="text-slate-400 absolute left-3 top-2.5 z-10"
            />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search PO, supplier…"
              className="pl-8 pr-4 py-2 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spin size="large" />
          </div>
        ) : pos.length === 0 ? (
          <Empty
            description="No purchase orders found."
            className="py-20"
          />
        ) : (
          <POTable
            data={pos}
            loading={isLoading}
            qData={queryData}
            refetch={refetch}
            total={totalPOs}
            onEdit={openEdit}
            onAdvanceStatus={(po) => setAdvancingPO(po)}
            onCancel={(po) => setCancellingPO(po)}
            onDelete={(id) => deletePO({ ids: id })}
            onHistory={setHistoryPO}
            onShare={(id) => generateShareLink(id)}
            onDownloadPdf={handleDownloadPdf}
          />
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={
          <span className="text-base font-semibold">
            {editingPO ? `Edit PO — ${editingPO.po_number}` : "New Purchase Order"}
          </span>
        }
        width={760}
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={creating || updating}
            onClick={() => form.submit()}
          >
            {editingPO ? "Save Changes" : "Create PO"}
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          size="middle"
          className="pt-2"
          onFinish={onFinish}
        >
          {/* Quotation selector — disabled when editing */}
          <Form.Item
            name="quotation_id"
            label={
              <span>
                Source Quotation{" "}
                <span className="text-xs text-slate-400 font-normal">
                  — optional, link to an accepted quotation
                </span>
              </span>
            }
          >
            <Select
              showSearch
              placeholder="Select an accepted quotation…"
              disabled={!!editingPO}
              filterOption={(input, option) =>
                ((option?.label as string) ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={quotationOptions}
              onChange={handleQuotationSelect}
              notFoundContent={
                <span className="text-xs text-slate-400 p-2 block">
                  No accepted quotations available. Ask clients to accept a
                  quote first.
                </span>
              }
            />
          </Form.Item>

          {/* Supplier details */}
          <div className="border border-slate-100 rounded-xl p-4 mb-4 bg-slate-50/40">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Supplier Details
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="supplier_name" label="Supplier Name" rules={[{ required: true, message: "Supplier name is required" }]}>
                <Input placeholder="e.g. HVAC Supplies Ltd" />
              </Form.Item>
              <Form.Item name="supplier_reference" label="Supplier Reference">
                <Input placeholder="Supplier's own order/ref number" />
              </Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item
                name="supplier_email"
                label="Supplier Email"
                rules={[
                  { type: "email", message: "Must be a valid email address" },
                ]}
              >
                <Input placeholder="orders@supplier.com" />
              </Form.Item>
              <Form.Item name="supplier_phone" label="Supplier Phone">
                <Input placeholder="+44 20 0000 0000" />
              </Form.Item>
            </div>
            <Form.Item name="supplier_address" label="Supplier Address">
              <Input.TextArea rows={2} placeholder="Full delivery address…" />
            </Form.Item>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="issue_date" label="Issue Date">
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item
              name="expected_delivery_date"
              label="Expected Delivery Date"
            >
              <DatePicker className="w-full" />
            </Form.Item>
          </div>

          {/* Line items */}
          <Form.Item label="Line Items" name="line_items">
            <LineItemsEditor />
          </Form.Item>

          {/* Financials */}
          <div className="grid grid-cols-3 gap-x-4">
            <Form.Item name="vat_rate" label="VAT Rate" initialValue={20}>
              <Select options={VAT_OPTS} />
            </Form.Item>
            <Form.Item name="discount_amount" label="Discount (£)">
              <InputNumber
                className="w-full"
                min={0}
                prefix="£"
                placeholder="0.00"
              />
            </Form.Item>
          </div>

          {/* Notes */}
          <Form.Item name="notes" label="Notes to Supplier">
            <Input.TextArea rows={2} placeholder="Delivery instructions or notes visible to supplier…" />
          </Form.Item>
          <Form.Item name="terms" label="Terms & Conditions">
            <Input.TextArea rows={2} placeholder="Payment terms, delivery conditions…" />
          </Form.Item>
          <Form.Item name="internal_notes" label="Internal Notes">
            <Input.TextArea
              rows={2}
              placeholder="Private notes — not visible to supplier…"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Advance Status Modal */}
      <AdvanceStatusModal
        po={advancingPO}
        qData={queryData}
        onClose={() => setAdvancingPO(null)}
      />

      {/* Cancel PO Modal */}
      <CancelModal
        po={cancellingPO}
        qData={queryData}
        onClose={() => setCancellingPO(null)}
      />

      {historyPO && (
        <StatusHistoryModal
          open={!!historyPO}
          entityType="purchase_order"
          entityId={historyPO.id}
          entityLabel={historyPO.po_number}
          onClose={() => setHistoryPO(null)}
        />
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
